import React, { useEffect, useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import { apiCall } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import ConfirmationModal from '../components/ConfirmationModal';
import { ToastContext } from '../App';

interface Slot {
  id: number;
  date: string;
  start_time: string;
  end_time: string;
  is_blocked?: boolean;
}

interface Booking {
  id: number;
  availability_slot_id: number;
  user_name: string;
  animal_name: string;
}

const AvailabilityPage: React.FC = () => {
  const { user } = useAuth();
  
  // Debug: mostrar informações do utilizador
  console.log('AvailabilityPage - User:', user);
  console.log('AvailabilityPage - User role:', user?.role);
  
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ date: '', start_time: '', end_time: '' });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [showExistingSlots, setShowExistingSlots] = useState(false);
  const { showToast } = React.useContext(ToastContext);
  
  // Estados para modais de confirmação
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [showDeleteSingleModal, setShowDeleteSingleModal] = useState(false);
  const [slotToDelete, setSlotToDelete] = useState<number | null>(null);

  useEffect(() => {
    // Definir data atual como padrão
    const today = new Date().toISOString().split('T')[0];
    setForm(prev => ({ ...prev, date: today }));
  }, []);

  // Carregar disponibilidades quando a data muda
  useEffect(() => {
    if (form.date) {
      loadAvailabilitySlots();
    }
  }, [form.date]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const formatTime = (timeString: string) => {
    return timeString.substring(0, 5); // Remove segundos se existirem
  };

  const handleCreateAvailability = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!form.date || !form.start_time || !form.end_time) {
      setFormError('Preencha todos os campos.');
      return;
    }
    setFormLoading(true);
    try {
      const response = await apiCall('/availability', {
        method: 'POST',
        body: JSON.stringify({
          date: form.date,
          start_time: form.start_time,
          end_time: form.end_time
        })
      });

      // Limpar apenas os campos de hora, manter a data
      setForm(prev => ({ ...prev, start_time: '', end_time: '' }));
      
      // Mostrar mensagem detalhada baseada no resultado
      let message;
      if (response.created_slots > 0 && response.ignored_slots > 0) {
        message = `Disponibilidades criadas com sucesso! (${response.created_slots} slots criadas, ${response.ignored_slots} slots já existiam)`;
      } else if (response.created_slots > 0) {
        message = `Disponibilidades criadas com sucesso! (${response.created_slots} slots criadas)`;
      } else {
        message = `Todas as slots já existem para este período (${response.ignored_slots} slots ignoradas)`;
      }
      
      showToast(message, 'success');
      
      // Carregar automaticamente as disponibilidades da data
      loadAvailabilitySlots();
    } catch (error: any) {
      setFormError(error.message || 'Erro ao criar disponibilidades');
      showToast(error.message || 'Erro ao criar disponibilidades', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const showDeleteAllConfirmation = () => {
    if (!form.date) {
      showToast('Selecionar uma data', 'error');
      return;
    }
    setShowDeleteAllModal(true);
  };

  const handleDeleteAllAvailability = async () => {
    try {
      const response = await apiCall(`/availability/date/${form.date}`, {
        method: 'DELETE'
      });
      const message = `Disponibilidades eliminadas com sucesso! (${response.deleted_slots} slots eliminadas, ${response.preserved_slots} slots com aulas preservadas)`;
      showToast(message, 'success');
      loadAvailabilitySlots(); // Recarregar a lista
      setShowDeleteAllModal(false);
    } catch (error: any) {
      showToast(error.message || 'Erro ao eliminar disponibilidades', 'error');
    }
  };

  const loadAvailabilitySlots = async () => {
    if (!form.date) {
      return;
    }

    setLoading(true);
    try {
      const [slotsResponse, bookingsResponse] = await Promise.all([
        apiCall(`/availability/date/${form.date}`),
        apiCall('/bookings/all')
      ]);

      setSlots(slotsResponse.slots || []);
      setBookings(bookingsResponse.bookings || []);
      setShowExistingSlots(true);
    } catch (error: any) {
      showToast('Erro ao carregar disponibilidades', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showDeleteSingleConfirmation = (slotId: number) => {
    setSlotToDelete(slotId);
    setShowDeleteSingleModal(true);
  };

  const handleDeleteSingleSlot = async () => {
    if (!slotToDelete) return;

    try {
      await apiCall(`/availability/slot/${slotToDelete}`, {
        method: 'DELETE'
      });
      showToast('Disponibilidade eliminada com sucesso!', 'success');
      loadAvailabilitySlots(); // Recarregar a lista
      setShowDeleteSingleModal(false);
      setSlotToDelete(null);
    } catch (error: any) {
      showToast(error.message || 'Erro ao eliminar disponibilidade', 'error');
    }
  };

  const renderAvailabilitySlots = () => {
    if (!slots || slots.length === 0) {
      return (
        <div className="col-12">
          <p className="text-center text-muted">Nenhuma disponibilidade para esta data.</p>
        </div>
      );
    }

    return slots.map(slot => {
      const booking = bookings.find(b => b.availability_slot_id === slot.id);
      
      return (
        <div className="col-md-6 col-lg-4 mb-3" key={slot.id}>
          <div className={`card availability-slot ${booking ? 'booked' : ''}`}>
            <div className="card-header bg-light">
              <strong>{formatTime(slot.start_time)} - {formatTime(slot.end_time)}</strong>
            </div>
            <div className="card-body">
              {booking ? (
                <>
                  <div className="text-success">
                    <i className="fas fa-user me-1"></i>{booking.user_name} com {booking.animal_name}
                  </div>
                  <small className="text-muted">Aula marcada - não pode ser eliminada</small>
                </>
              ) : (
                <>
                  <div className="text-primary">
                    <i className="fas fa-clock me-1"></i>Disponível
                  </div>
                  <button 
                    className="btn btn-sm btn-outline-danger mt-2"
                    onClick={() => showDeleteSingleConfirmation(slot.id)}
                  >
                    <i className="fas fa-trash me-1"></i>Eliminar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <div className="container py-4">
      <div className="row">
        <div className="col-12">
          <div className="card shadow">
            <div className="card-header bg-success text-white">
              <h5><i className="fas fa-clock me-2"></i>Gestão de Disponibilidades</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleCreateAvailability}>
                <div className="row">
                  <div className="col-md-4">
                    <label htmlFor="availabilityDate" className="form-label">Data:</label>
                    <input 
                      type="date" 
                      className="form-control" 
                      id="availabilityDate"
                      name="date"
                      value={form.date}
                      onChange={handleFormChange}
                      required 
                    />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="startTime" className="form-label">Hora de Início:</label>
                    <input 
                      type="time" 
                      className="form-control" 
                      id="startTime"
                      name="start_time"
                      value={form.start_time}
                      onChange={handleFormChange}
                      required 
                    />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="endTime" className="form-label">Hora de Fim:</label>
                    <input 
                      type="time" 
                      className="form-control" 
                      id="endTime"
                      name="end_time"
                      value={form.end_time}
                      onChange={handleFormChange}
                      required 
                    />
                  </div>
                </div>
                <div className="mt-3 d-flex flex-column flex-md-row">
                  <button 
                    type="submit" 
                    className="btn btn-success mb-2 mb-md-0"
                    disabled={formLoading}
                  >
                    {formLoading ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-1"></i>A criar...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-plus me-1"></i>Adicionar Disponibilidade
                      </>
                    )}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-danger ms-md-2"
                    onClick={showDeleteAllConfirmation}
                  >
                    <i className="fas fa-trash me-1"></i>Apagar Todas
                  </button>
                </div>
                {formError && <div className="alert alert-danger mt-3">{formError}</div>}
              </form>
              
              {/* Área para mostrar as disponibilidades existentes */}
              {showExistingSlots && (
                <div className="mt-4">
                  <h6 className="text-success">
                    <i className="fas fa-list me-2"></i>Disponibilidades Existentes
                  </h6>
                  {loading ? (
                    <LoadingSpinner />
                  ) : (
                    <div className="row">
                      {renderAvailabilitySlots()}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Toast global */}
      {/* The ToastContext handles this */}

      {/* Modal de Confirmação para Eliminar Todas as Disponibilidades */}
      <ConfirmationModal
        isOpen={showDeleteAllModal}
        title="Eliminar Todas as Disponibilidades"
        message="Tem certeza que deseja eliminar TODAS as disponibilidades disponíveis desta data?\n\nNota: As slots que têm aulas marcadas NÃO serão eliminadas."
        confirmText="Eliminar Todas"
        cancelText="Cancelar"
        confirmButtonClass="btn-danger"
        onConfirm={handleDeleteAllAvailability}
        onCancel={() => setShowDeleteAllModal(false)}
      />

      {/* Modal de Confirmação para Eliminar Disponibilidade Individual */}
      <ConfirmationModal
        isOpen={showDeleteSingleModal}
        title="Eliminar Disponibilidade"
        message="Tem certeza que deseja eliminar esta disponibilidade?"
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmButtonClass="btn-danger"
        onConfirm={handleDeleteSingleSlot}
        onCancel={() => {
          setShowDeleteSingleModal(false);
          setSlotToDelete(null);
        }}
      />
    </div>
  );
};

export default AvailabilityPage; 